const { conf } = require('./configService');
const { zeroFill } = require('../utils/stringUtils');

const dateService = {
  format: (
    timestampOrDateObject,
    format,
    { locale, useSiteTimeZone = true } = {}
  ) => {
    const dateLocale = locale || conf.langcode;
    const date =
      timestampOrDateObject instanceof Date
        ? timestampOrDateObject
        : new Date(timestampOrDateObject);
    let formattedDate = '';
    const siteTimeZone = useSiteTimeZone ? conf.defaultTimeZone : undefined;
    switch (format) {
      case 'time': {
        formattedDate = date.toLocaleTimeString(dateLocale, {
          hour: '2-digit',
          minute: '2-digit',
          timeZone: siteTimeZone
        });
        formattedDate = formattedDate.replace(/^(\d{1}:\d{2})$/, '0$1');
        break;
      }
      case 'show_time': {
        formattedDate = date.toLocaleDateString(dateLocale, {
          weekday: 'long',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          timeZone: siteTimeZone
        });
        formattedDate = formattedDate.replace(/ (\d{1}:\d{2})$/, ' 0$1');
        break;
      }
      case 'u': {
        formattedDate = date.toLocaleDateString(dateLocale, {
          weekday: 'long',
          day: 'numeric',
          month: 'long',
          year: 'numeric',
          timeZone: siteTimeZone
        });

        break;
      }
      case 'weekday': {
        formattedDate = date.toLocaleDateString(dateLocale, {
          weekday: 'long',
          timeZone: siteTimeZone
        });

        break;
      }
      case 'd MM Y': {
        formattedDate = date.toLocaleDateString(dateLocale, {
          day: 'numeric',
          month: 'long',
          year: 'numeric',
          timeZone: siteTimeZone
        });

        break;
      }
      case 'long': {
        formattedDate = date.toLocaleDateString(dateLocale, {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
          timeZone: siteTimeZone
        });
        break;
      }
      case 'article_date': {
        formattedDate = date.toLocaleDateString(dateLocale, {
          month: 'short',
          year: 'numeric',
          day: 'numeric',
          hour: 'numeric',
          minute: 'numeric',
          hour12: false,
          timeZoneName: 'short',
          timeZone: siteTimeZone
        });
        formattedDate = formattedDate.replace('UTC+1', 'CET');
        formattedDate = formattedDate.replace('UTC+2', 'CEST');
        formattedDate = formattedDate.replace('GMT-5', 'CDT');
        formattedDate = formattedDate.replace('GMT-6', 'CST');
        if (siteTimeZone === 'Europe/Lisbon') {
          formattedDate = formattedDate.replace('WET', '');
          formattedDate = formattedDate.replace('WEST', '');
        }
        break;
      }
      case 'Y/m/d':
      case 'd/m/Y':
      case 'Y-m-d':
      case 'm/d/Y': {
        formattedDate = format
          .replace('Y', date.getFullYear().toString())
          .replace('m', `0${date.getMonth() + 1}`.slice(-2))
          .replace('d', `0${date.getDate()}`.slice(-2));
        break;
      }
      case 'iso': {
        formattedDate = date.toISOString();
        break;
      }
      case 'iso-no-milliseconds': {
        formattedDate = date.toISOString().replace(/\.\d{3}Z$/, 'Z');
        break;
      }
      default:
    }
    return formattedDate;
  },

  secondsToMinutes: seconds => {
    const minutes = zeroFill(Math.floor(seconds / 60), 2);
    const remainingSeconds = zeroFill(seconds - minutes * 60, 2);
    return `${minutes}:${remainingSeconds}`;
  },

  // Takes "01:21" and returns 81
  minutesToSeconds: minutesAndSeconds => {
    if (
      minutesAndSeconds ||
      typeof minutesAndSeconds !== 'string' ||
      minutesAndSeconds.includes(':')
    ) {
      return minutesAndSeconds;
    }
    const [minutes, seconds] = minutesAndSeconds.split(':');
    return Number.parseInt(minutes, 10) * 60 + Number.parseInt(seconds, 10);
  },

  // @see https://stackoverflow.com/questions/57737451/convert-seconds-to-iso-8601-duration-with-javascript
  // @see https://rgxdb.com/r/MD2234J to check format is valid
  secondsToIso8601: seconds => {
    const designations = [
      ['D', 60 * 60 * 24],
      ['H', 60 * 60],
      ['M', 60],
      ['S', 1]
    ];

    let duration = 'P';
    let tSign = '';
    let remainder = seconds;

    designations.forEach(([sign, secs]) => {
      const value = Math.floor(remainder / secs);
      remainder %= secs;
      if (value) {
        tSign = sign !== 'D' && tSign === '' ? 'T' : '';
        duration += `${tSign}${value}${sign}`;
      }
    });

    if (duration === 'P') {
      duration = 'PT0S';
    }

    return duration;
  },

  changeTimezone: (date, timezone) => {
    // Need to clone date to avoid changing the passed object.
    const baseDate = dateService.clone(date);
    const dateInTimezone = new Date(
      baseDate.toLocaleString('en-US', {
        timeZone: timezone
      })
    );
    const diff = baseDate.getTime() - dateInTimezone.getTime();
    return new Date(date.getTime() + diff);
  },

  getStartOfWeek: (date, weekStartDay) => {
    const weekStartDayNumber = weekStartDay || conf.weekStartDay;
    // Need to clone date to avoid changing the passed object.
    const weekStartDate = dateService.clone(date);
    weekStartDate.setDate(
      weekStartDate.getDate() - weekStartDate.getDay() + weekStartDayNumber
    );
    if (weekStartDayNumber === 1 && date.getDay() === 0) {
      dateService.addDay(weekStartDate, -7);
    }
    return weekStartDate;
  },

  getEndOfWeek: (date, weekStartDay) => {
    const startOfWeek = dateService.getStartOfWeek(date, weekStartDay);
    return dateService.addDay(startOfWeek, 6);
  },

  clone: date => new Date(date),

  addDay: (date, numberOfDays) => {
    date.setDate(date.getDate() + numberOfDays);
    return date;
  },

  isWithinDateRange: (currentDate, start, end) => {
    const endDate = end || 0;
    const startDate = start || 0;
    return endDate
      ? currentDate > startDate && currentDate < endDate
      : currentDate > startDate;
  }
};
module.exports = { dateService };
